import { isNil } from 'lodash';
import { useState } from 'react';
import { StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { Pill } from '@shared/components/pill';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody2 } from '@shared/components/typography';
import { isDeletedEntity } from '@shared/data/entity/Entity';
import { RelatedItem } from '@shared/data/entity/fields/RelatedEntityField';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { useResponsiveFormContext } from '../../ResponsiveFormContext';
import { AddMoreButton } from '../AddMoreButton';
import { MAX_CARDS_TO_SHOW } from './constants';

interface Props {
  entities: API.WorkflowEntity[];
  items: RelatedItem[];
  onItemPress?: (entity?: API.WorkflowEntity) => void;
  onItemRemove?: (item?: RelatedItem) => void;
  openPicker: VoidFunction;
  isReadOnly: boolean;
  component: API.RelatedCardComponent;
  openingEntity: API.WorkflowEntity | null;
  loading: boolean;
}

export function RelatedEntityList({
  entities,
  items,
  onItemPress,
  onItemRemove,
  openPicker,
  isReadOnly,
  component,
  openingEntity,
  loading,
}: Props) {
  const responsiveForms = useResponsiveFormContext();
  const count = entities.length;
  const [showAll, setShowAll] = useState(false);
  const renderShowMore = count > MAX_CARDS_TO_SHOW;
  const remainingCount = count - MAX_CARDS_TO_SHOW;
  const itemsToShow = showAll ? items : items.slice(0, MAX_CARDS_TO_SHOW);
  const defaultEntityName = i18n('workflowTemplate.defaultRelatedCard', {
    workflowName: component.label,
  });

  return (
    <View>
      <MBView style={styles.rowWrap}>
        {itemsToShow.map(item => {
          return (
            <RelatedEntityPill
              key={item.id}
              item={item}
              entities={entities}
              onItemPress={onItemPress}
              onItemRemove={onItemRemove}
              loading={loading}
              openingEntity={openingEntity}
              defaultEntityName={defaultEntityName}
            />
          );
        })}
      </MBView>
      {!isReadOnly && !responsiveForms && (
        <AddMoreButton component={component} onPress={openPicker} />
      )}
      {renderShowMore && (
        <MBTouchableOpacity onPress={() => setShowAll(prev => !prev)}>
          <MBView style={styles.showMore}>
            <MBTextBody2>
              {!showAll && i18n('card.blocks.cardsInput.overflow', { count: remainingCount })}
              {showAll && i18n('card.list.showLess')}
            </MBTextBody2>
          </MBView>
        </MBTouchableOpacity>
      )}
    </View>
  );
}

interface RelatedEntityPillProps
  extends Omit<Props, 'items' | 'component' | 'isReadOnly' | 'openPicker'> {
  item: RelatedItem;
  defaultEntityName: string;
}

function RelatedEntityPill({
  item,
  defaultEntityName,
  entities,
  onItemPress,
  onItemRemove,
  loading,
  openingEntity,
}: RelatedEntityPillProps) {
  const entity = entities.find(e => e.id === item.id);
  const entityName = entity?.fields?.name;
  const isMissingName = isNil(entityName);
  const isDeleted = isDeletedEntity(entity);

  return (
    <Pill
      key={item.id}
      sections={[entityName ?? defaultEntityName]}
      italic={isDeleted || isMissingName}
      maxWidth={250}
      onPress={isDeleted ? undefined : () => onItemPress?.(entity)}
      onRemove={() => onItemRemove?.(item)}
      loading={loading || (!isNil(entity?.id) && entity?.id === openingEntity?.id)}
    />
  );
}

const styles = StyleSheet.create({
  rowWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginLeft: -grid(1),
  },
  showMore: {
    marginLeft: grid(1),
    marginRight: grid(1),
  },
});
